/**
 * Inventory list hook for the modal's Inventory tab. Wraps the existing
 * `GET_LOCATION_VEHICLE_INVENTORY` (`getVehicleInventoryV2`) query with
 * debounced search + pagination. Apollo's default cache means repeat
 * page/search combinations are served without re-fetching.
 *
 * @module vehicleModal/hooks/useInventoryVehicles
 */
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { GET_LOCATION_VEHICLE_INVENTORY } from "../../../../graphql/query";
import { INVENTORY_PAGE_SIZE } from "../constants";

const SEARCH_DEBOUNCE_MS = 400;

/**
 * Resolve the active location id(s) from session storage. Supports the
 * comma-separated "all locations" form the app stores.
 * @returns {number[]}
 */
function resolveLocationIds() {
  const raw = (typeof window !== "undefined" && sessionStorage.getItem("currentLocationId")) || "";
  if (!raw) return [];
  return raw
    .split(",")
    .map(id => parseInt(id, 10))
    .filter(id => !Number.isNaN(id));
}

/**
 * @param {{ enabled?: boolean }} [opts] - skip the query until the tab is shown
 * @returns {{
 *   rows: import("../types").InventoryVehicle[],
 *   count: number,
 *   loading: boolean,
 *   error: import("@apollo/client").ApolloError|undefined,
 *   search: string,
 *   setSearch: (v: string) => void,
 *   page: number,
 *   setPage: (p: number) => void,
 *   pageSize: number,
 *   setPageSize: (s: number) => void,
 * }}
 */
export default function useInventoryVehicles({ enabled = true } = {}) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(INVENTORY_PAGE_SIZE);

  const locationIds = useMemo(resolveLocationIds, []);

  // Debounce the search box; reset to page 1 whenever the term changes.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, loading, error } = useQuery(GET_LOCATION_VEHICLE_INVENTORY, {
    skip: !enabled,
    fetchPolicy: "cache-first",
    variables: {
      locationIds,
      search: debouncedSearch || undefined,
      page,
      pageSize,
    },
  });

  const result = data?.getVehicleInventoryV2;

  return {
    rows: result?.data || [],
    count: result?.count || 0,
    loading,
    error,
    search: searchInput,
    setSearch: setSearchInput,
    page,
    setPage,
    pageSize,
    setPageSize,
  };
}
